import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { cn } from 'modules/Common/utils/cn';
import CgPopover from './CgPopover';

/**
 * The house ⓘ: a small info glyph that opens an explanation on hover.
 *
 * Every ⓘ in the app is this component (docs/features/info-hint-standardization).
 * One glyph, one size, one colour, one hit target: 11px `faCircleInfo` in
 * slate-300 rising to slate-500 on hover, always a `<button>` so it is
 * reachable from the keyboard and announces what it opens.
 *
 * The popover is the standard CgPopover body — an optional title strip over
 * `px-3 py-2 text-sm text-gray-500` — so an explanation reads the same as
 * every other popover (IH1). This component adds no padding, type size or
 * colour of its own; width goes through `maxWidth` (IH3). Paragraphs inside
 * reset with `mb-0`, so the `space-y-1.5` sets the gap between them.
 *
 * @param {React.ReactNode} children   The explanation; `<p className="mb-0">` blocks.
 * @param {React.ReactNode} [title]    Rendered in the CgPopover title strip.
 * @param {string} [label]             Accessible name. Defaults to `title` when that
 *                                     is a string, else "How this is worked out".
 * @param {string} [subject]           The field this ⓘ sits on; names it "About
 *                                     <subject>" so it never shares the field's own
 *                                     accessible name. Wins over `label`.
 * @param {string} [position]          Radix side; `bottom` unless the trigger
 *                                     sits at the foot of the viewport.
 * @param {string} [maxWidth]          CgPopover `max-w-[…]` class.
 * @param {boolean} [stopPropagation]  Swallow the trigger's click, for an ⓘ that
 *                                     sits inside a clickable row, card or header.
 * @param {string} [popoverContentClassName]  e.g. a z-index to clear a dialog.
 * @param {string} [triggerClassName]  Layout, or a deliberate status colour.
 * @param {string} [testId]            `data-testid` on the trigger.
 *
 * Keyboard focus opens it as hover does (and leaving closes it): CgPopover only
 * listens for the mouse, so the open state is held here. A click or tap opens
 * it. Radix sets `aria-expanded` and `aria-haspopup` on the button.
 */
export default function CgInfoHint({
  children,
  title = null,
  label = null,
  subject = null,
  position = 'bottom',
  maxWidth = 'max-w-[300px]',
  stopPropagation = false,
  popoverContentClassName = '',
  triggerClassName = '',
  testId,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const name =
    typeof subject === 'string' && subject
      ? t('common:actions.aboutField', 'About {{label}}', { label: subject })
      : ([label, title].find((v) => typeof v === 'string' && v) ??
        t('common:actions.explain', 'How this is worked out'));
  return (
    <CgPopover
      trigger="hover"
      position={position}
      openDelay={100}
      closeDelay={200}
      open={open}
      onOpenChange={setOpen}
      maxWidth={maxWidth}
      popoverTitle={title}
      popoverContentClassName={popoverContentClassName}
      triggerElement={
        <button
          type="button"
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          // A click focuses (and so opens) it: Radix's own toggle would shut it again.
          // Inside a clickable row, card or header it also keeps the click there.
          onClick={(e) => {
            e.preventDefault();
            if (stopPropagation) e.stopPropagation();
            setOpen(true);
          }}
          className={cn(
            'relative inline-flex shrink-0 items-center rounded-full p-[6.5px] -m-[6.5px] align-middle transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40',
            'text-slate-300 hover:text-slate-500',
            triggerClassName,
          )}
          aria-label={name}
          data-testid={testId}
        >
          <FontAwesomeIcon
            icon={faCircleInfo}
            className="text-[11px]"
          />
        </button>
      }
      popoverContent={<div className="space-y-1.5">{children}</div>}
    />
  );
}

/**
 * One line of an ⓘ explanation, already translated. `**…**` in the string
 * renders bold, so the few words a reader should catch at a glance stand out.
 * The string goes through `t()` at the call site, where the extractor and
 * the literal gate can see it.
 */
export const InfoLine = ({ text, className = '' }) => (
  <p className={cn('mb-0', className)}>
    {String(text ?? '')
      .split(/\*\*(.*?)\*\*/g)
      .map((part, i) =>
        i % 2 ? (
          <strong
            key={i}
            className="font-semibold text-gray-700"
          >
            {part}
          </strong>
        ) : (
          part
        ),
      )}
  </p>
);
